/**
 * @fileoverview Repertoire assistant — conversational AI tool for instructor lesson prep.
 *
 * Two-step flow: instructor asks a question → Hans clarifies → instructor confirms →
 * Hans generates a concise teaching guide. Optionally select a student for tailored advice.
 */

import { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send, BookOpen, User, Loader2, RotateCcw, ChevronDown, Search,
  Bookmark, Check, X, Trash2, Lock, ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import { accountFeatureEnabled } from "@/lib/orgFeatures";
import api, { getErrorMessage } from "@/lib/api";
import { Card } from "@/components/studio/primitives";
import { RepertoireGuide } from "@/components/RepertoireGuide";
import { effectiveTier, hasAiFeatures, tierDisplayName } from "@/lib/tiers";
import { useAiDisclosureGate } from "@/components/AiDisclosureDialog";
import { isAiDisclosureRequiredError } from "@/lib/aiDisclosure";

// The plan that unlocks personalization, named once.
const AI_PLAN_NAME = tierDisplayName("concertmaster");

const RepertoirePage = () => {
  const location = useLocation();
  const { user, setUser, refreshUser } = useContext(AuthContext);
  // A tailored guide sends the student's name, interests and recent lesson
  // summaries to OpenAI, which the AI disclosure covers.
  const { ensureAiDisclosure, aiDisclosureDialog } = useAiDisclosureGate({ user, setUser, refreshUser });
  // Personalization is the PREMIUM AI set, which is a narrower question than
  // "is this plan paid" — the $19 Studio tier is paid and has no AI at all.
  //
  // Asked of the RESOLVED tier, never the stored `subscription_tier`. The
  // stored field is what Stripe or Apple set, and it is `basic` for the two
  // kinds of account that are entitled without paying us directly: a
  // grandfathering pin (X7 — which is how a Founding Ensemble member holds
  // Studio AI) and coverage by a paying org. Both were reading as free here
  // and had student tailoring padlocked, while `routes/repertoire.py` — which
  // asks `org_service.effective_tier_for` — was perfectly willing to
  // personalize. `effective_tier` on /auth/me is that same resolution.
  const isBasic = !hasAiFeatures(effectiveTier(user));

  // An org can turn the whole repertoire surface off (org_service `repertoire`,
  // enforced account-wide at the router). The sidebar already hides the nav
  // item, but this page is still reachable by bookmark or deep link — without
  // this the API 403s land in a `.catch` and the page renders a normal-looking
  // empty state. The server 403 stays authoritative; this only explains it.
  const repertoireDisabled = !accountFeatureEnabled(user, "repertoire");

  // Students list for optional student selector
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const studentDropdownRef = useRef(null);

  // Conversation state
  const [query, setQuery] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | clarifying | confirming | generating | done
  const [clarification, setClarification] = useState(null); // { clarification, options, type }
  const [result, setResult] = useState(null); // { title, type, sections }
  const [refineInput, setRefineInput] = useState("");
  const [showRefine, setShowRefine] = useState(false);

  // History of past interactions
  const [history, setHistory] = useState([]);

  // Saved repertoires
  const [savedItems, setSavedItems] = useState([]);
  const [savedPanelOpen, setSavedPanelOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [viewingSaved, setViewingSaved] = useState(null); // saved item being viewed

  const scrollRef = useRef(null);
  const savedPanelRef = useRef(null);
  // Abort in-flight repertoire POSTs (up to 90s each) if the page unmounts.
  const requestAbortRef = useRef(null);
  useEffect(() => () => requestAbortRef.current?.abort(), []);

  // Fetch students and saved repertoires on mount. Skipped when the org turned
  // repertoire off — every /repertoire route 403s, so there's nothing to fetch.
  useEffect(() => {
    if (repertoireDisabled) return undefined;
    const controller = new AbortController();
    const ignoreAbort = (err) => {
      if (!controller.signal.aborted) console.error(err);
    };
    api.get("/students", { signal: controller.signal })
      .then((res) => setStudents(res.data))
      .catch(ignoreAbort);
    api.get("/repertoire/saved", { signal: controller.signal })
      .then((res) => setSavedItems(res.data))
      .catch(ignoreAbort);
    return () => controller.abort();
  }, [repertoireDisabled]);

  // Open a specific repertoire when navigated from student profile
  useEffect(() => {
    if (location.state?.viewRepertoire) {
      setViewingSaved(location.state.viewRepertoire);
    }
  }, [location.state]);

  // Auto-run a question passed via navigation state (once) — e.g. a deep link
  // with an initialQuery. (The Home "Ask Hans" hero that used this was removed.)
  const didInitQueryRef = useRef(false);
  useEffect(() => {
    if (didInitQueryRef.current) return;
    const initial = location.state?.initialQuery?.trim();
    if (initial) {
      didInitQueryRef.current = true;
      handleSubmit(null, initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Close student dropdown on outside click
  useEffect(() => {
    if (!studentDropdownOpen) return;
    const handleClick = (e) => {
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(e.target)) {
        setStudentDropdownOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClick);
    return () => document.removeEventListener("pointerdown", handleClick);
  }, [studentDropdownOpen]);

  // Close saved panel on outside click
  useEffect(() => {
    if (!savedPanelOpen) return;
    const handleClick = (e) => {
      if (savedPanelRef.current && !savedPanelRef.current.contains(e.target)) {
        setSavedPanelOpen(false);
      }
    };
    document.addEventListener("pointerdown", handleClick);
    return () => document.removeEventListener("pointerdown", handleClick);
  }, [savedPanelOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [phase, history, result]);

  // Reset saved state when starting a new generation
  useEffect(() => {
    if (phase === "clarifying") { setIsSaved(false); setViewingSaved(null); }
  }, [phase]);

  const handleSave = async () => {
    if (!result || isSaved || savingInProgress) return;
    setSavingInProgress(true);
    try {
      const res = await api.post("/repertoire/save", {
        title: result.title,
        type: result.type,
        query,
        sections: result.sections,
        student_id: selectedStudent?.student_id || null,
        student_name: selectedStudent
          ? `${selectedStudent.first_name} ${selectedStudent.last_name}`.trim()
          : null,
      });
      setSavedItems((prev) => [res.data, ...prev]);
      setIsSaved(true);
      toast.success("Teaching guide saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingInProgress(false);
    }
  };

  const handleDeleteSaved = async (repertoireId) => {
    try {
      await api.delete(`/repertoire/saved/${repertoireId}`);
      setSavedItems((prev) => prev.filter((s) => s.repertoire_id !== repertoireId));
      if (viewingSaved?.repertoire_id === repertoireId) setViewingSaved(null);
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleViewSaved = (item) => {
    setViewingSaved(item);
    setSavedPanelOpen(false);
    // Reset current conversation state to show the saved item
    setPhase("idle");
    setQuery("");
    setClarification(null);
    setResult(null);
  };

  const handleSubmit = async (e, overrideQuery) => {
    e?.preventDefault();
    const q = (overrideQuery ?? query).trim();
    if (overrideQuery) setQuery(overrideQuery);
    if (!q || phase === "clarifying" || phase === "generating") return;

    setPhase("clarifying");
    setClarification(null);
    setResult(null);
    setShowRefine(false);

    const controller = new AbortController();
    requestAbortRef.current = controller;
    try {
      const res = await api.post("/repertoire/clarify", { query: q }, { timeout: 90000, signal: controller.signal });
      setClarification(res.data);
      setPhase("confirming");
    } catch (err) {
      if (controller.signal.aborted) return;
      toast.error(getErrorMessage(err, "Failed to process your question"));
      setPhase("idle");
    }
  };

  const handleConfirm = async () => {
    // The server only tailors for a plan with AI features; a generic guide
    // carries no student data.
    const tailored = Boolean(selectedStudent?.student_id) && !isBasic;
    setPhase("generating");
    const controller = new AbortController();
    requestAbortRef.current = controller;
    const generate = () => api.post("/repertoire/generate", {
      query,
      confirmed: clarification.clarification,
      type: clarification.type,
      student_id: selectedStudent?.student_id || null,
    }, { timeout: 90000, signal: controller.signal });
    try {
      if (tailored && !(await ensureAiDisclosure())) {
        setPhase("confirming");
        return;
      }
      let res;
      try {
        res = await generate();
      } catch (err) {
        if (!tailored || !isAiDisclosureRequiredError(err)) throw err;
        // The cached account was out of date: the server says it is owed.
        if (!(await ensureAiDisclosure({ force: true }))) {
          setPhase("confirming");
          return;
        }
        res = await generate();
      }
      setResult(res.data);
      setPhase("done");
    } catch (err) {
      if (controller.signal.aborted) return;
      toast.error(getErrorMessage(err, "Failed to generate teaching guide"));
      setPhase("confirming");
    }
  };

  const handleRefine = async () => {
    const refined = refineInput.trim();
    if (!refined) return;
    setQuery(refined);
    setShowRefine(false);
    setRefineInput("");
    setPhase("clarifying");
    setClarification(null);

    const controller = new AbortController();
    requestAbortRef.current = controller;
    try {
      const res = await api.post("/repertoire/clarify", { query: refined }, { timeout: 90000, signal: controller.signal });
      setClarification(res.data);
      setPhase("confirming");
    } catch (err) {
      if (controller.signal.aborted) return;
      toast.error(getErrorMessage(err, "Failed to process your question"));
      setPhase("idle");
    }
  };

  const handleNewQuestion = () => {
    // Save current to history
    if (result) {
      setHistory((prev) => [...prev, { query, clarification, result, student: selectedStudent }]);
    }
    setQuery("");
    setPhase("idle");
    setClarification(null);
    setResult(null);
    setShowRefine(false);
    setRefineInput("");
    setIsSaved(false);
    setViewingSaved(null);
  };

  const filteredStudents = students.filter((s) => {
    const name = `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase();
    return name.includes(studentSearch.toLowerCase());
  });

  if (repertoireDisabled) {
    const orgName = (user?.org_memberships || []).find((m) => m.features?.repertoire === false)?.org_name;
    return (
      <div className="min-h-screen bg-[var(--studio-base)]">
        <main className="mx-auto flex max-w-lg flex-col items-center px-6 py-24 text-center">
          <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--studio-interactive)]">
            <Lock className="h-6 w-6 text-[var(--text-tertiary)]" strokeWidth={1.5} />
          </div>
          <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Repertoires are turned off</h1>
          <p className="font-sans text-sm leading-6 text-[var(--text-tertiary)]">
            {orgName
              ? `${orgName} has turned the repertoire assistant off for your account.`
              : "Your organization has turned the repertoire assistant off for your account."}{" "}
            Your saved repertoires are safe — they'll come back if it's switched on again.
          </p>
          <Card className="mt-8 w-full text-left">
            <p className="font-sans text-[13px] leading-6 text-[var(--text-tertiary)]">
              Think this is a mistake? Your organization's admin can turn it back on from their Hans org portal.
            </p>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--studio-base)]">
      <main className="flex min-h-screen flex-col">
        {/* Header */}
        <div className="border-b border-[var(--studio-divider)] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
            <h1 className="text-lg font-medium text-[var(--text-primary)]">Repertoires</h1>
          </div>

          <div className="flex items-center gap-3">
          {/* Saved library toggle */}
          <button
            onClick={() => setSavedPanelOpen(!savedPanelOpen)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
              savedPanelOpen
                ? "border-[var(--accent-yellow-border)] text-[var(--accent-yellow-text)] bg-[var(--accent-yellow-faint)]"
                : "border-[var(--studio-divider-strong)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:border-[var(--studio-divider-strong)]"
            }`}
          >
            <Bookmark className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Saved</span>
            {savedItems.length > 0 && (
              <span className="text-xs bg-[var(--studio-tint-strong)] px-1.5 py-0.5 rounded-full">{savedItems.length}</span>
            )}
          </button>

          {/* Student selector */}
          <div className="relative" ref={studentDropdownRef}>
            {isBasic ? (
              <div className="relative group">
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed border-[var(--studio-divider-strong)] text-[var(--text-quaternary)] bg-[var(--studio-tint)] cursor-not-allowed select-none">
                  <Lock className="w-3.5 h-3.5" strokeWidth={2} />
                  Student tailoring
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                </div>
                <div className="absolute right-0 top-full mt-2 w-56 bg-[var(--studio-overlay)] border border-[var(--studio-divider-strong)] rounded-xl p-3 text-xs text-[var(--text-tertiary)] shadow-[var(--studio-shadow)] z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity">
                  <p className="font-medium text-[var(--text-primary)] mb-1">{AI_PLAN_NAME} feature</p>
                  <p>Upgrade to {AI_PLAN_NAME} to tailor repertoire responses to a specific student's history and level.</p>
                </div>
              </div>
            ) : (
            <button
              onClick={() => setStudentDropdownOpen(!studentDropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                selectedStudent
                  ? "border-[var(--accent-yellow-border-strong)] text-[var(--accent-yellow-text)] bg-[var(--accent-yellow-dim)] shadow-[0_0_12px_var(--accent-yellow-glow)]"
                  : "border-dashed border-[var(--studio-divider-strong)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--studio-divider-strong)] bg-[var(--studio-tint)]"
              }`}
            >
              <User className="w-4 h-4" strokeWidth={2} />
              {selectedStudent ? `${selectedStudent.first_name} ${selectedStudent.last_name}`.trim() : "Select a student"}
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
            )}

            {studentDropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-72 glass border border-[var(--studio-divider-strong)] rounded-xl shadow-[var(--studio-shadow)] z-50 overflow-hidden">
                <div className="p-2 border-b border-[var(--studio-divider)]">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-tertiary)]" />
                    <input
                      type="text"
                      placeholder="Search students..."
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      className="w-full bg-[var(--studio-interactive)] border border-[var(--studio-divider-strong)] rounded-lg pl-8 pr-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] outline-hidden focus:border-[var(--accent-yellow-border-strong)]"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto p-1">
                  <button
                    onClick={() => { setSelectedStudent(null); setStudentDropdownOpen(false); setStudentSearch(""); }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      !selectedStudent ? "text-[var(--accent-yellow-text)] bg-[var(--studio-tint-strong)]" : "text-[var(--text-secondary)] hover:bg-[var(--studio-tint)]"
                    }`}
                  >
                    No student (general)
                  </button>
                  {filteredStudents.map((s) => (
                    <button
                      key={s.student_id}
                      onClick={() => { setSelectedStudent(s); setStudentDropdownOpen(false); setStudentSearch(""); }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        selectedStudent?.student_id === s.student_id
                          ? "text-[var(--accent-yellow-text)] bg-[var(--studio-tint-strong)]"
                          : "text-[var(--text-primary)] hover:bg-[var(--studio-tint)]"
                      }`}
                    >
                      {`${s.first_name} ${s.last_name}`.trim()}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Saved panel overlay */}
        <AnimatePresence>
          {savedPanelOpen && (
            <motion.div
              ref={savedPanelRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full sm:w-80 glass border-l border-[var(--studio-divider)] z-50 flex flex-col"
            >
              <div className="flex items-center justify-between p-4 border-b border-[var(--studio-divider)]">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-4 h-4 text-[var(--accent-yellow-text)]" strokeWidth={1.5} />
                  <h2 className="text-[var(--text-primary)] font-medium text-sm">Saved Guides</h2>
                </div>
                <button
                  onClick={() => setSavedPanelOpen(false)}
                  className="p-1.5 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--studio-interactive)] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {savedItems.length === 0 ? (
                  <p className="text-[var(--text-tertiary)] text-sm text-center py-8">No saved guides yet</p>
                ) : (
                  savedItems.map((item) => (
                    <div
                      key={item.repertoire_id}
                      className="group bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-xl p-3 hover:border-[var(--studio-divider-strong)] transition-colors cursor-pointer"
                      onClick={() => handleViewSaved(item)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[var(--text-primary)] text-sm font-medium truncate">{item.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-[var(--text-tertiary)] bg-[var(--studio-interactive)] px-1.5 py-0.5 rounded-full">
                              {item.type}
                            </span>
                            {item.student_name && (
                              <span className="text-xs text-[var(--text-tertiary)] truncate">
                                {item.student_name}
                              </span>
                            )}
                          </div>
                          <p className="text-[var(--text-quaternary)] text-xs mt-1">
                            {new Date(item.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteSaved(item.repertoire_id); }}
                          className="p-1 rounded text-[var(--text-quaternary)] hover:text-[var(--state-danger)] opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {/* The panel is a quick reach for the last few guides; the whole
                  library — searchable, with confirmed deletes — is its own page. */}
              <div className="border-t border-[var(--studio-divider)] p-3">
                <Link
                  to="/repertoire/saved"
                  data-testid="saved-panel-view-all"
                  onClick={() => setSavedPanelOpen(false)}
                  className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 font-sans text-sm font-semibold text-[var(--accent-yellow-text)] transition-colors hover:bg-[var(--studio-interactive)]"
                >
                  View all saved guides
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Conversation area */}
        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6 pb-32 space-y-6">
          {/* History */}
          {history.map((item, idx) => (
            <HistoryBlock key={idx} item={item} />
          ))}

          {/* Viewing a saved item */}
          {viewingSaved && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary)] mb-2">
                <Bookmark className="w-3.5 h-3.5" />
                Saved guide — {new Date(viewingSaved.created_at).toLocaleDateString()}
                {viewingSaved.student_name && ` — for ${viewingSaved.student_name}`}
              </div>
              <RepertoireGuide result={viewingSaved} />
              <div className="flex justify-center pt-2">
                <button
                  onClick={() => setViewingSaved(null)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--studio-interactive)] transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  New question
                </button>
              </div>
            </motion.div>
          )}

          {/* Current interaction */}
          {!viewingSaved && <AnimatePresence mode="wait">
            {phase === "idle" && history.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mx-auto max-w-[620px] py-16 text-center"
              >
                <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-[11px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] shadow-[0_0_16px_var(--accent-yellow-glow)]">
                  <BookOpen className="h-5 w-5 text-[var(--accent-yellow-text)]" strokeWidth={1.6} />
                </div>
                <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--accent-yellow-text)]">Repertoires</div>
                <h2 className="mt-3 font-serif text-[40px] font-normal leading-[1.05] tracking-[-0.02em] text-[var(--text-primary)]">
                  Ask Hans about<br />anything musical
                </h2>
                <p className="mx-auto mt-4 max-w-[520px] font-serif text-[18px] leading-[1.5] text-[var(--text-tertiary)]">
                  Pieces, techniques, cadences, scales, concepts — get concise teaching guides tailored to help you prepare for lessons.
                </p>
                {selectedStudent && !isBasic && (
                  <p className="mt-3 font-sans text-sm text-[var(--accent-yellow-text)]">
                    Responses will be tailored for {`${selectedStudent.first_name} ${selectedStudent.last_name}`.trim()}
                  </p>
                )}
                {isBasic && (
                  <p className="mt-3 flex items-center justify-center gap-1 font-sans text-xs text-[var(--text-tertiary)]">
                    <Lock className="h-3 w-3" /> Upgrade to {AI_PLAN_NAME} for student-tailored responses
                  </p>
                )}
                <div className="mt-8 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Try asking</div>
                <div className="mt-4 flex flex-wrap justify-center gap-2.5">
                  {["How do I teach legato?", "Spiccato exercises for beginners", "Bach Invention No. 4 fingering", "Vocal warmups for tenors"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleSubmit(null, s)}
                      className="rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-2.5 font-sans text-[14px] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* User query bubble */}
            {phase !== "idle" && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-end"
              >
                <div className="bg-[var(--studio-interactive)] border border-[var(--studio-divider-strong)] rounded-2xl rounded-br-md px-4 py-3 max-w-lg">
                  <p className="text-[var(--text-primary)] text-sm">{query}</p>
                  {selectedStudent && (
                    <p className="text-[var(--text-tertiary)] text-xs mt-1">
                      For {`${selectedStudent.first_name} ${selectedStudent.last_name}`.trim()}
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Clarifying spinner */}
            {phase === "clarifying" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 text-[var(--text-tertiary)] text-sm"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                Hans is thinking...
              </motion.div>
            )}

            {/* Clarification response with options */}
            {(phase === "confirming" || phase === "generating" || phase === "done") && clarification && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl rounded-bl-md px-4 py-3 max-w-lg space-y-3">
                  <p className="text-[var(--text-primary)] text-sm">{clarification.clarification}</p>
                  {phase === "confirming" && (
                    <div className="flex flex-wrap gap-2">
                      {clarification.options.map((opt) => (
                        <button
                          key={opt}
                          onClick={() => {
                            if (opt === "Yes") handleConfirm();
                            else { setShowRefine(true); }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            opt === "Yes"
                              ? "bg-[var(--accent-yellow)] text-[var(--text-inverse)] hover:brightness-110"
                              : "bg-[var(--studio-interactive)] text-[var(--text-secondary)] hover:bg-[var(--studio-overlay)]"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  {phase === "confirming" && showRefine && (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={refineInput}
                        onChange={(e) => setRefineInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleRefine()}
                        placeholder="Describe what you're looking for..."
                        className="flex-1 bg-[var(--studio-base)] border border-[var(--studio-divider-strong)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] outline-hidden focus:border-[var(--accent-yellow-border-strong)]"
                        autoFocus
                      />
                      <button
                        onClick={handleRefine}
                        className="px-3 py-2 bg-[var(--accent-yellow)] text-[var(--text-inverse)] rounded-lg text-sm font-medium hover:brightness-110 transition-colors"
                      >
                        Go
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Generating spinner */}
            {phase === "generating" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 text-[var(--text-tertiary)] text-sm"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating teaching guide...
              </motion.div>
            )}

            {/* Result display */}
            {phase === "done" && result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <RepertoireGuide result={result} />
                <div className="flex justify-center gap-3 pt-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaved || savingInProgress}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      isSaved
                        ? "text-[var(--state-success)] bg-[var(--state-success-dim)] cursor-default"
                        : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--studio-interactive)]"
                    }`}
                  >
                    {savingInProgress ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isSaved ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Bookmark className="w-4 h-4" />
                    )}
                    {isSaved ? "Saved" : "Save"}
                  </button>
                  <button
                    onClick={handleNewQuestion}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--studio-interactive)] transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    New question
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>}

          <div ref={scrollRef} />
        </div>

        {/* Input bar - fixed at bottom */}
        {!viewingSaved && (phase === "idle" || phase === "done") && (
          <div className="fixed bottom-0 left-0 right-0 md:left-64 border-t border-[var(--studio-divider)] glass p-4 pb-safe z-40">
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto flex gap-3">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about a piece, technique, cadence, scale..."
                className="flex-1 bg-[var(--studio-interactive)] border border-[var(--studio-divider-strong)] rounded-xl px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] outline-hidden focus:border-[var(--accent-yellow-border-strong)] transition-colors"
                autoFocus
              />
              <button
                type="submit"
                disabled={!query.trim()}
                className="px-4 py-3 bg-[var(--accent-yellow)] text-[var(--text-inverse)] rounded-xl font-medium hover:brightness-110 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                <Send className="w-4 h-4" strokeWidth={2} />
                <span className="hidden sm:inline">Ask Hans</span>
              </button>
            </form>
          </div>
        )}
      </main>
      {aiDisclosureDialog}
    </div>
  );
};

/** Display a past query/response interaction. */
const HistoryBlock = ({ item }) => (
  <div className="space-y-3 opacity-60 border-b border-[var(--studio-divider)] pb-6">
    {/* User query */}
    <div className="flex justify-end">
      <div className="bg-[var(--studio-interactive)] border border-[var(--studio-divider-strong)] rounded-2xl rounded-br-md px-4 py-3 max-w-lg">
        <p className="text-[var(--text-primary)] text-sm">{item.query}</p>
        {item.student && (
          <p className="text-[var(--text-tertiary)] text-xs mt-1">
            For {`${item.student.first_name} ${item.student.last_name}`.trim()}
          </p>
        )}
      </div>
    </div>
    {/* Hans clarification */}
    {item.clarification && (
      <div className="flex justify-start">
        <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl rounded-bl-md px-4 py-3 max-w-lg">
          <p className="text-[var(--text-secondary)] text-sm">{item.clarification.clarification}</p>
        </div>
      </div>
    )}
    {/* Result */}
    {item.result && <RepertoireGuide result={item.result} compact />}
  </div>
);

export default RepertoirePage;
